import PropTypes from 'prop-types'
import React from 'react'
import { Form, Field } from 'react-final-form'
import { Transition, TransitionGroup } from 'react-transition-group'
import { css } from 'styled-components'

import ButtonRoundedSt from 'components/ButtonRoundedSt'
import Icon from 'components/Icon'
import { ResizeContext } from 'contexts/Resize'
import {
  composeValidators,
  errorCustom,
  required,
  notEqual,
} from 'forms/validations'
import { TextLH } from 'styles/components/typography'

import {
  ButtonWrapper,
  FormInner,
  FormStyled,
  InputWrapper,
  PromocodeAnimation,
  Wrapper,
  IconBox,
  StatusWrapper,
  StatusIcon,
  CloseWrapper,
  ResetWrapper,
  ButtonText,
  CartItemTitleStyled,
  ButtonRoundedStStyled,
  TextFieldStyled,
  CartItemTitleButton,
} from './styles'

const FORM_ID = 'form-promocode'
const ANIMATION_DURATION = 300
const { DYNAMIC_DISCOUNT_PROMO } = process.env.localization

/**
 * Promocode component
 *
 * @returns {Component} PromoCode
 */

const PromoCodeNew = ({
  inputName,
  promocodeId,
  handleClose,
  handleSubmit,
  buttonTitle,
  errorCode,
  submitErrors,
  texts,
  isFetching,
  isDisabled,
}) => {
  // 0 - open awaiting
  // 1 - enter awaiting
  // 2 - successfully used
  // 3 - cannot be used
  const [condition, setCondition] = React.useState(
    isDisabled ? 3 : promocodeId ? 2 : 0,
  )
  const [promoIdState, setPromoIdState] = React.useState(promocodeId)
  const isFirstRender = React.useRef(true)
  const prevPromocode = React.useRef(promoIdState)
  const { mobile } = React.useContext(ResizeContext)

  React.useEffect(() => {
    setPromoIdState(prevState => {
      prevPromocode.current = prevState
      return promocodeId
    })
  }, [promocodeId])

  React.useEffect(() => {
    if (!isFetching) {
      const isNotFormState = condition === 2 || condition === 3
      if (!promoIdState && isNotFormState) {
        setCondition(0)
      } else if (promoIdState && promoIdState !== prevPromocode.current) {
        setCondition(2)
      }
    }
  }, [isFetching, condition, promoIdState])

  const handleOpen = React.useCallback(() => {
    setCondition(1)
  }, [])

  const handleClosed = React.useCallback(() => {
    setCondition(0)
  }, [])

  const handleReset = React.useCallback(() => {
    setCondition(0)
    handleSubmit({ promocode: null })
  }, [handleSubmit])

  React.useEffect(() => {
    if (!condition && !isFirstRender.current) {
      handleClose()
    }
  }, [condition, handleClose])

  React.useEffect(() => {
    isFirstRender.current = false
  }, [])

  React.useEffect(() => {
    if (isDisabled) {
      setCondition(3)
    }
  }, [isDisabled])

  let content = null

  switch (condition) {
    case 1:
      content = (
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit, submitting }) => (
            <FormStyled id={FORM_ID} onSubmit={handleSubmit}>
              <FormInner>
                <CloseWrapper onClick={handleClosed}>
                  <IconBox>
                    <Icon id="btn_close" color="black" />
                  </IconBox>
                </CloseWrapper>

                <InputWrapper>
                  <TextFieldStyled
                    autoComplete="off"
                    withoutErrorIndent={true}
                    placeholder={texts.enter_button}
                    name={inputName}
                    validate={composeValidators([
                      errorCustom(texts.errors.empty_field, required),
                      errorCustom(
                        texts.errors.not_valid,
                        notEqual(DYNAMIC_DISCOUNT_PROMO),
                      ),
                    ])}
                    isDisabled={isFetching}
                    submitErrors={submitErrors}
                    forceSubmitError={texts.errors[errorCode]}
                  />
                </InputWrapper>

                <ButtonWrapper>
                  <ButtonRoundedSt
                    isDisabled={submitting || isFetching}
                    as="button"
                    bgStyles={css`
                      background-color: ${p => p.theme.colors.grass};

                      ${p => p.theme.mq.upMd} {
                        background-color: ${p => p.theme.colors.lilacDark};
                      }
                    `}
                    onClick={handleSubmit}
                  >
                    <ButtonText
                      dangerouslySetInnerHTML={{ __html: buttonTitle }}
                    />
                  </ButtonRoundedSt>
                </ButtonWrapper>

                <Field
                  name="submit"
                  type="submit"
                  component="input"
                  hidden={true}
                  disabled={submitting || isFetching}
                />
              </FormInner>
            </FormStyled>
          )}
        />
      )
      break
    case 2:
      content = (
        <>
          <StatusWrapper>
            <StatusIcon bgColor="grass">
              <Icon id="btn_check_xs" color="white" />
            </StatusIcon>
            <TextLH dangerouslySetInnerHTML={{ __html: texts.success }} />
          </StatusWrapper>
          <ResetWrapper>
            <ButtonRoundedSt
              as="button"
              bgStyles={css`
                background-color: ${p => p.theme.colors.transparent};
              `}
              size={'fit'}
              blackoutStyles={css`
                background-color: ${p => p.theme.colors.transparent};
              `}
              textStyles={css`
                color: ${p => p.theme.colors.black};
                opacity: 0.4;
                transition: opacity 0.3s;

                ${p => p.theme.mq.hover} {
                  &:hover {
                    opacity: 0.6;
                  }
                }
              `}
              onClick={handleReset}
            >
              <CartItemTitleStyled
                dangerouslySetInnerHTML={{
                  __html: mobile ? texts.reset_button_mob : texts.reset_button,
                }}
              />
            </ButtonRoundedSt>
          </ResetWrapper>
        </>
      )
      break
    case 3:
      content = (
        <StatusWrapper>
          <StatusIcon>
            <Icon id="color_attention" />
          </StatusIcon>
          <TextLH dangerouslySetInnerHTML={{ __html: texts.disabled }} />
        </StatusWrapper>
      )
      break
    default:
      content = (
        <ButtonRoundedStStyled
          forwardedAs="button"
          bgStyles={css`
            background-color: ${p => p.theme.colors.grayOp06NonTransp};
          `}
          onClick={handleOpen}
        >
          <CartItemTitleButton
            dangerouslySetInnerHTML={{ __html: texts.enter_button }}
          />
        </ButtonRoundedStStyled>
      )
  }

  return (
    <Wrapper isOpened={condition === 1}>
      <TransitionGroup>
        <Transition timeout={ANIMATION_DURATION} key={`block.${condition}`}>
          {status => (
            <PromocodeAnimation state={status}>{content}</PromocodeAnimation>
          )}
        </Transition>
      </TransitionGroup>
    </Wrapper>
  )
}

PromoCodeNew.propTypes = {
  /** Title of submit button */
  buttonTitle: PropTypes.string.isRequired,

  /** Handler for close of promocode input */
  handleClose: PropTypes.func.isRequired,

  /** Handler for submit */
  handleSubmit: PropTypes.func.isRequired,

  /** Name of input field */
  inputName: PropTypes.string.isRequired,

  /** Texts **/
  texts: PropTypes.object.isRequired,

  /** Flag of waiting response state */
  isFetching: PropTypes.bool,

  /** Promocode ID that has got from server */
  promocodeId: PropTypes.number,

  /** Flag to disable promocode field **/
  isDisabled: PropTypes.bool,

  /** Error messages from server for whole form */
  submitErrors: PropTypes.array,

  /** Error code from server **/
  errorCode: PropTypes.string,
}

PromoCodeNew.defaulProps = {
  isFetching: false,
}

export default PromoCodeNew
